/* =========================================================================
 * IBC 4.1 — MULTIPACK BUILDER
 * -------------------------------------------------------------------------
 * Drives snippets/ibc-multipack.liquid. The customer chooses N pieces from
 * a list, in any combination, and EACH CHOSEN PIECE GOES INTO THE BASKET AS
 * ITS OWN LINE, at its own real variant and its own real price.
 *
 * WHY N LINES AND NOT ONE
 * -----------------------
 * Shopify prices a line by its VARIANT. Nothing in a theme can make one
 * line cost the sum of things named in its properties — that needs a Cart
 * Transform Function, which is app work. So the pack is not a line: the
 * lines ARE the products, and the total is the sum because it could not be
 * anything else. Nothing to maintain when a price changes, and no ceiling
 * (a price ladder of pack variants would have needed 49 variants once the
 * dragees joined the spreads, and 308 once the pantry did — against a
 * 100-variant limit).
 *
 * The lines are drawn back together as one pack in the basket by the
 * `_Selection ID` they share. See snippets/ibc-cart-lines.liquid.
 *
 * THE WORD IS "SELECTION", EVERYWHERE A CUSTOMER CAN SEE IT
 * ---------------------------------------------------------
 * The line-item properties, the page copy and the basket all say Selection,
 * because that is what the shop calls this. The CODE still says pack — the
 * class names, the data attributes, the variable names, the CSS. That
 * boundary is deliberate and it is drawn in one place per file: renaming
 * every internal hook would be a large diff across four files and a
 * stylesheet for no reader-visible gain, and a half-applied rename of DOM
 * hooks is exactly the sort of thing that ships broken.
 *
 * THE PROPERTY CONTRACT, written identically on every line of a selection
 * -----------------------------------------------------------------------
 *   Selection                   PUBLIC. The selection's name plus its short
 *                               code, e.g. `Spreads 3-pack (selection A7K3)`.
 *                               Public because that is what puts it on the
 *                               admin order, the confirmation email and the
 *                               packing slip beside every piece — the answer
 *                               to "which selection was this chosen for". The
 *                               code is there so two identical selections on
 *                               one order can be told apart when they are
 *                               being picked and boxed.
 *   _Selection ID               private, unique per selection INSTANCE. What
 *                               the basket groups on and the editor finds a
 *                               selection by. Unique per build, so two
 *                               selections sharing a piece can never merge
 *                               into one line.
 *   _Selection Unit             private, how many of this piece are in ONE
 *                               selection. What lets the basket scale a
 *                               selection's quantity across every line
 *                               exactly, with no rounding.
 *   _Selection Handle           private, the builder to go back to.
 *   _Selection Allergens
 *   _Selection Trace Allergens
 *   _Selection Allergens Source private, the combined statement, on every
 *                               line so it travels with any one of them.
 *
 * HOW IT ADDS, AND WHY THERE ARE TWO WAYS
 * ---------------------------------------
 * The preferred way is the seam assets/ibc-product.js already publishes:
 *
 *     window.IbcProduct.addSubmitHook(function (ctx) {
 *       ctx.items.push(…);        // the pieces
 *       ctx.skipParent = true;    // and NOT the multipack product itself
 *     });
 *
 * That reuses the button's busy state, the inline error region, the focus
 * handling and the `ibc:cart-changed` dispatch — none of which is worth
 * reimplementing.
 *
 * `ctx.skipParent` is new. If assets/ibc-product.js has not been updated
 * yet — a half-installed package, which has bitten this theme before — the
 * flag would be ignored and the multipack product would be added ALONGSIDE
 * the pieces, at its own price, which is a wrong order rather than a wrong
 * layout. So support is not assumed: it is read from
 * `window.IbcProduct.capabilities.skipParent`, at submit time rather than
 * at boot, and when it is absent this file takes the submit over and posts
 * the pieces itself. Both paths build the SAME items array from the same
 * function, so they cannot drift.
 *
 * THE PACK MUST BE EXACTLY FULL. A three-pack with two in it is refused and
 * the reason is said out loud, in words — never by grey.
 *
 * THE `+` IS NEVER DISABLED. Pressing it on a full pack explains why the
 * pack is full rather than doing nothing. A disabled control at the limit
 * is the commonest way to leave someone stuck with no idea what is wrong.
 *
 * ES5, no dependencies, and it never touches anything outside its own root
 * and the total slots that opt in with `[data-mp-total-value]`.
 * ====================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-ibc-multipack]';

  /* U+00D7. Written as an escape rather than as the character so that a
   * file saved in the wrong encoding cannot turn it into mojibake in
   * somebody's order — which is a thing that has happened to this shop's
   * property values before. */
  var TIMES = '\u00D7';

  /* `?selection=<id>` — deliberately NOT `?edit=`, which the bundle already owns
   * and which carries a LINE KEY. A pack is not one line, and a line key is
   * not stable: /cart/change.js re-keys a line whenever its properties
   * change. The pack id is the only handle that survives an edit. */
  var EDIT_PARAM = 'selection';

  /* Unambiguous when read aloud down a phone or off a packing slip:
   * no I, L, O, 0 or 1. */
  var CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

  function root_url() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function parseJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (e) {
      /* A malformed island must not take the page with it. The pack still
       * builds; it just cannot state its allergens, which `sourceOf` below
       * then reports honestly as 'unknown'. */
      return null;
    }
  }

  function stop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  }

  /* ------------------------------------------------------------- money -- */

  /* THE FORMAT IS LEARNED FROM LIQUID, NOT GUESSED AT.
   *
   * A running total is a number the customer just invented by choosing, so
   * no Liquid filter can have printed it — but every OTHER price on the page
   * was printed by `| money`, and a total in a different shape beside them
   * reads as a different kind of number. `Intl.NumberFormat` would follow
   * the BROWSER's locale, so a visitor from Paris would see `12,50 £` under
   * a grid of `£12.50` tiles.
   *
   * So the snippet prints one known amount — 123456 through `| money` — into
   * `data-mp-money-sample`, and the shape is read back off it. Whatever the
   * shop's money format is, the total matches it exactly, and it keeps
   * matching if the format is ever changed. */
  function moneyShape(sample) {
    var out = { prefix: '', suffix: '', thousands: ',', decimal: '.', precision: 2 };
    if (!sample) return out;

    var first = sample.search(/[0-9]/);
    if (first === -1) return out;

    var last = sample.length - 1;
    while (last >= 0 && !/[0-9]/.test(sample.charAt(last))) last -= 1;

    out.prefix = sample.slice(0, first);
    out.suffix = sample.slice(last + 1);

    var core = sample.slice(first, last + 1);

    /* `1,234.56` · `1.234,56` · `1 234,56` · `1234.56` */
    var m = core.match(/^\d(\D?)\d{3}(\D)\d{2}$/);
    if (m) {
      out.thousands = m[1];
      out.decimal = m[2];
      out.precision = 2;
      return out;
    }

    /* `1,235` — a no-decimals format */
    m = core.match(/^\d(\D?)\d{3}$/);
    if (m) {
      out.thousands = m[1];
      out.decimal = '';
      out.precision = 0;
      return out;
    }

    return out;
  }

  /* Integer arithmetic throughout. Cents never become a float, so a pack of
   * three at 1250 is 3750 and not 3749.9999999999995. */
  function formatCents(cents, shape) {
    var value = Math.round(Number(cents) || 0);
    var negative = value < 0;
    var n = Math.abs(value);
    var whole;
    var frac = '';

    if (shape.precision === 2) {
      whole = String(Math.floor(n / 100));
      frac = String(n % 100);
      while (frac.length < 2) frac = '0' + frac;
    } else {
      whole = String(Math.round(n / 100));
    }

    var grouped = '';
    while (whole.length > 3) {
      grouped = shape.thousands + whole.slice(whole.length - 3) + grouped;
      whole = whole.slice(0, whole.length - 3);
    }
    grouped = whole + grouped;

    var body = frac ? grouped + shape.decimal + frac : grouped;
    return (negative ? '-' : '') + shape.prefix + body + shape.suffix;
  }

  /* --------------------------------------------------------- pack ident -- */

  function newPack() {
    var code = '';
    for (var i = 0; i < 4; i += 1) {
      code += CODE_ALPHABET.charAt(Math.floor(Math.random() * CODE_ALPHABET.length));
    }
    /* Time first so a pack id sorts by when it was built, code second so
     * two packs built in the same millisecond still differ. */
    return { id: new Date().getTime().toString(36).toUpperCase() + '-' + code, code: code };
  }

  /* ============================================================ builder == */

  function Multipack(root) {
    this.root = root;
    this.capacity = parseInt(root.getAttribute('data-mp-capacity'), 10);
    if (isNaN(this.capacity) || this.capacity < 1) this.capacity = 0;

    this.handle = root.getAttribute('data-mp-handle') || '';
    this.title = root.getAttribute('data-mp-title') || '';
    this.shape = moneyShape(root.getAttribute('data-mp-money-sample'));

    this.tiles = [];
    var nodes = root.querySelectorAll('[data-mp-piece]');
    for (var i = 0; i < nodes.length; i++) {
      this.tiles.push({
        el: nodes[i],
        handle: nodes[i].getAttribute('data-handle') || '',
        variantId: parseInt(nodes[i].getAttribute('data-variant-id'), 10) || 0,
        title: nodes[i].getAttribute('data-title') || '',
        price: parseInt(nodes[i].getAttribute('data-price'), 10) || 0,
        qtyEl: nodes[i].querySelector('[data-mp-qty]'),
        lessEl: nodes[i].querySelector('[data-mp-less]'),
        moreEl: nodes[i].querySelector('[data-mp-more]'),
        qty: 0
      });
    }

    this.chosenEl = root.querySelector('[data-mp-chosen]');
    this.progressEl = root.querySelector('[data-mp-progress]');
    this.noteEl = root.querySelector('[data-mp-note]');
    this.allergens = parseJSON(root.querySelector('[data-mp-allergens]')) || {};

    this.form = root.closest ? root.closest('form[action*="/cart/add"]') : null;

    this.editing = '';      /* the `_Selection ID` being edited, when there is one */
    this.priorLabel = null; /* the Add to basket wording, kept so it can go back */
    this.hooked = false;

    this.onClick = this.handleClick.bind(this);
    this.onSubmit = this.handleSubmitCapture.bind(this);
    this.onHook = this.contributeToAdd.bind(this);
  }

  Multipack.prototype.start = function () {
    if (!this.capacity || !this.tiles.length) return;
    this.root.addEventListener('click', this.onClick);

    /* CAPTURE, ON THE DOCUMENT, ON PURPOSE.
     *
     * `submit` targets the form, and listeners on the target itself run in
     * REGISTRATION order regardless of their capture flag — so a listener
     * added here could not reliably get in front of the one <ibc-product>
     * added when it upgraded. A capturing listener on `document` runs
     * before the event reaches the form at all, which is the only place
     * this file can stand if it is ever to stop the default add. */
    document.addEventListener('submit', this.onSubmit, true);

    this.ensureHook();
    this.sync();
  };

  Multipack.prototype.destroy = function () {
    this.root.removeEventListener('click', this.onClick);
    document.removeEventListener('submit', this.onSubmit, true);
    if (this.hooked && window.IbcProduct && window.IbcProduct.removeSubmitHook) {
      window.IbcProduct.removeSubmitHook(this.onHook);
    }
    this.hooked = false;
  };

  /* --------------------------------------------------------------- state -- */

  Multipack.prototype.total = function () {
    var n = 0;
    for (var i = 0; i < this.tiles.length; i++) n += this.tiles[i].qty;
    return n;
  };

  /* One pack's worth, in cents. Not multiplied by the quantity stepper: the
   * price slot on a product page is a unit price everywhere else on this
   * shop, and a pack is the unit here. */
  Multipack.prototype.totalCents = function () {
    var n = 0;
    for (var i = 0; i < this.tiles.length; i++) n += this.tiles[i].qty * this.tiles[i].price;
    return n;
  };

  Multipack.prototype.money = function (cents) {
    return formatCents(cents, this.shape);
  };

  Multipack.prototype.tileFor = function (node) {
    for (var i = 0; i < this.tiles.length; i++) {
      if (this.tiles[i].el === node) return this.tiles[i];
    }
    return null;
  };

  Multipack.prototype.handleClick = function (event) {
    var target = event.target;
    var less = target.closest ? target.closest('[data-mp-less]') : null;
    var more = target.closest ? target.closest('[data-mp-more]') : null;
    if (!less && !more) return;

    var host = (less || more).closest('[data-mp-piece]');
    var tile = this.tileFor(host);
    if (!tile) return;

    if (less) {
      if (tile.qty > 0) tile.qty -= 1;
      this.say('');
    } else {
      if (this.total() >= this.capacity) {
        /* FULL, AND SAID SO. The press is not swallowed and the button was
         * never dead — the pack simply has no room, and the way out is to
         * take something out first. */
        this.say(
          'Your selection is full at ' +
            this.capacity +
            '. Take one out to swap it for something else.'
        );
        return;
      }
      tile.qty += 1;
      this.say('');
    }
    this.sync();
  };

  Multipack.prototype.say = function (text) {
    if (!this.noteEl) return;
    this.noteEl.textContent = text || '';
    this.root.setAttribute('data-mp-state', text ? 'warning' : 'ok');
  };

  Multipack.prototype.sync = function () {
    var total = this.total();

    for (var i = 0; i < this.tiles.length; i++) {
      var t = this.tiles[i];
      if (t.qtyEl) t.qtyEl.textContent = String(t.qty);
      if (t.lessEl) t.lessEl.disabled = t.qty === 0;
      t.el.setAttribute('data-mp-chosen', t.qty > 0 ? 'true' : 'false');
    }

    if (this.chosenEl) this.chosenEl.textContent = String(total);
    if (this.progressEl) {
      var ratio = this.capacity ? total / this.capacity : 0;
      if (ratio > 1) ratio = 1;
      this.progressEl.style.transform = 'scaleX(' + ratio + ')';
    }
    this.root.setAttribute('data-mp-full', total === this.capacity ? 'true' : 'false');

    this.paint();
  };

  /* EVERY TOTAL SLOT ON THE PAGE, WRITTEN BY ONE FUNCTION. There are two —
   * the line under the grid and the page's own price slot, which is above
   * the fold and nowhere near it — and they are found by the same attribute
   * rather than by two selectors, so a third could be added in Liquid alone
   * and they still could not disagree.
   *
   * `data-mp-total-empty`, when a slot carries it, is what that slot says
   * before anything has been chosen. The price slot uses it: a bare £0.00 at
   * the top of a product page reads as a bug, and this theme has shipped
   * that exact bug before with the gift cards. */
  Multipack.prototype.paint = function () {
    var cents = this.totalCents();
    var text = this.money(cents);
    var slots = document.querySelectorAll('[data-mp-total-value]');

    for (var i = 0; i < slots.length; i++) {
      var empty = slots[i].getAttribute('data-mp-total-empty');
      var blank = cents === 0 && !!empty;
      slots[i].textContent = blank ? empty : text;
      /* The state is an attribute rather than a class so that Liquid can emit
       * the same one on first paint and the slot is never briefly wrong. */
      slots[i].setAttribute('data-mp-total-state', blank ? 'empty' : 'value');
    }
  };

  /* ---------------------------------------------------------- properties -- */

  Multipack.prototype.chosen = function () {
    var out = [];
    for (var i = 0; i < this.tiles.length; i++) {
      if (this.tiles[i].qty > 0) out.push(this.tiles[i]);
    }
    return out;
  };

  /* `2 × Pistachio, 1 × Coffee`. Not a line-item property any more — every
   * piece is its own line and says its own name — but the basket's pack
   * header and the tests both want the one-line reading of a selection. */
  Multipack.prototype.summary = function () {
    var picked = this.chosen();
    var parts = [];
    for (var i = 0; i < picked.length; i++) {
      parts.push(picked[i].qty + ' ' + TIMES + ' ' + picked[i].title);
    }
    return parts.join(', ');
  };

  /* THE PACK'S COMBINED ALLERGEN STATEMENT, de-duplicated case-insensitively
   * and sorted, from the island Liquid emitted.
   *
   * `source` is the honest part. `complete` means every chosen piece had a
   * statement; `partial` means some did; `unknown` means none did. A pack
   * that cannot say what is in it must not print an empty list, because an
   * empty list reads as "free of them". */
  Multipack.prototype.allergenSet = function (key) {
    var picked = this.chosen();
    var seen = {};
    var out = [];
    var withData = 0;

    for (var i = 0; i < picked.length; i++) {
      var rec = this.allergens[picked[i].handle];
      if (!rec) continue;
      var list = rec[key];
      if (!list || !list.length) continue;
      withData += 1;
      for (var j = 0; j < list.length; j++) {
        var value = String(list[j]).replace(/^\s+|\s+$/g, '');
        if (!value) continue;
        var fold = value.toLowerCase();
        if (seen[fold]) continue;
        seen[fold] = true;
        out.push(value);
      }
    }

    out.sort(function (a, b) {
      return a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : 0;
    });

    return { list: out, withData: withData, of: picked.length };
  };

  Multipack.prototype.sourceOf = function (a, t) {
    var covered = a.withData > t.withData ? a.withData : t.withData;
    if (!a.of) return 'unknown';
    if (covered === 0) return 'unknown';
    if (covered === a.of) return 'complete';
    return 'partial';
  };

  /* THE ONE PLACE THE BASKET PAYLOAD IS BUILT. Both add paths call it, so
   * neither can carry a property the other does not. */
  Multipack.prototype.itemsFor = function (packs, pack) {
    var picked = this.chosen();
    var a = this.allergenSet('a');
    var t = this.allergenSet('t');
    var source = this.sourceOf(a, t);
    var name = this.title + ' (selection ' + pack.code + ')';
    var count = packs > 0 ? packs : 1;
    var items = [];

    for (var i = 0; i < picked.length; i++) {
      var props = {};
      props['Selection'] = name;
      props['_Selection ID'] = pack.id;
      props['_Selection Unit'] = String(picked[i].qty);
      props['_Selection Handle'] = this.handle;
      /* An empty allergen property would read as "free of them" wherever it
       * surfaced, so a pack with nothing to say says nothing at all. The
       * source key is always written: `unknown` is itself the statement. */
      if (a.list.length) props['_Selection Allergens'] = a.list.join(', ');
      if (t.list.length) props['_Selection Trace Allergens'] = t.list.join(', ');
      props['_Selection Allergens Source'] = source;

      items.push({
        id: picked[i].variantId,
        quantity: picked[i].qty * count,
        properties: props
      });
    }

    return items;
  };

  Multipack.prototype.packsWanted = function () {
    if (!this.form) return 1;
    var input = this.form.querySelector('[name="quantity"]');
    if (!input) return 1;
    var n = parseInt(input.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  };

  Multipack.prototype.setPacksWanted = function (n) {
    if (!this.form) return;
    var input = this.form.querySelector('[name="quantity"]');
    if (input) input.value = String(n > 0 ? n : 1);
  };

  /* The message a short or overfull pack gets, in words, in both places a
   * customer might be looking. */
  Multipack.prototype.shortfall = function () {
    var total = this.total();
    if (total === this.capacity) return '';
    return total < this.capacity
      ? 'Choose ' + (this.capacity - total) + ' more before adding this to your basket.'
      : 'That is more than this pack holds. Take ' + (total - this.capacity) + ' out.';
  };

  /* ------------------------------------------------------------ the add -- */

  function supportsSkipParent() {
    return !!(
      window.IbcProduct &&
      window.IbcProduct.capabilities &&
      window.IbcProduct.capabilities.skipParent &&
      typeof window.IbcProduct.addSubmitHook === 'function'
    );
  }

  /* Read at submit time as well as at boot. Both files are `defer`, so the
   * order they execute in is the order their tags appear — which is not
   * this file's to depend on. */
  Multipack.prototype.ensureHook = function () {
    if (this.hooked) return true;
    if (!supportsSkipParent()) return false;
    window.IbcProduct.addSubmitHook(this.onHook);
    this.hooked = true;
    return true;
  };

  /* THE PREFERRED PATH. Runs inside <ibc-product>.addToCart(), before the
   * POST, with the button already busy and the error region already wired.
   *
   * `ctx.skipParent` is the whole point: the multipack product itself must
   * NEVER reach the basket. It exists to host this builder and to be a page
   * worth linking to; its own price is not what the pack costs. */
  Multipack.prototype.contributeToAdd = function (ctx) {
    if (!this.capacity) return;
    if (!document.documentElement.contains(this.root)) return;

    /* An edit is not an add, and the capture listener should already have
     * taken the submit. Belt and braces: refuse rather than silently post a
     * second pack. */
    if (this.editing) {
      throw this.refuse(
        'We could not update your selection. Please try again, or add it fresh.'
      );
    }

    if (this.total() !== this.capacity) {
      throw this.refuse(this.shortfall());
    }

    var items = this.itemsFor(this.packsWanted(), newPack());
    if (!items.length) {
      throw this.refuse(this.shortfall() || 'Choose your pieces before adding this to your basket.');
    }

    for (var i = 0; i < items.length; i++) ctx.items.push(items[i]);
    ctx.skipParent = true;
    this.say('');
  };

  /* Said in the builder's own note AND handed to the product engine, which
   * prints it in the inline error region and moves focus to `focusTarget`. */
  Multipack.prototype.refuse = function (message) {
    this.say(message);
    var error = new Error(message);
    var first = this.root.querySelector('[data-mp-more]');
    if (first) error.focusTarget = first;
    return error;
  };

  /* THE FALLBACK PATH, for a half-installed package: assets/ibc-product.js
   * is still the old one, does not know `skipParent`, and would post the
   * multipack product alongside the pieces at its own price. Rather than
   * ship a wrong order, this file takes the submit over and posts the same
   * items itself.
   *
   * It is not a lesser path — it is the same `itemsFor`, the same endpoint
   * and the same event. What it lacks is the button's busy and error
   * chrome, which belongs to the product engine and is not worth cloning. */
  Multipack.prototype.addOwn = function () {
    var self = this;

    if (this.total() !== this.capacity) {
      this.say(this.shortfall());
      var first = this.root.querySelector('[data-mp-more]');
      if (first && first.focus) first.focus();
      return Promise.resolve();
    }

    var items = this.itemsFor(this.packsWanted(), newPack());
    if (!items.length) return Promise.resolve();

    this.busy(true);
    return this.postItems(items)
      .then(function () {
        return self.announce('multipack');
      })
      .then(function () {
        self.busy(false);
        self.say('');
      })
      .catch(function (error) {
        self.busy(false);
        self.say((error && error.message) || 'Sorry, that could not be added to your basket.');
      });
  };

  Multipack.prototype.postItems = function (items) {
    return fetch(root_url() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(
            data.description || data.message || 'Sorry, that could not be added to your basket.'
          );
        }
        return data;
      });
    });
  };

  Multipack.prototype.fetchCart = function () {
    return fetch(root_url() + 'cart.js', { headers: { Accept: 'application/json' } }).then(
      function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      }
    );
  };

  /* /cart/add.js RETURNS THE ADDED LINES, NOT THE CART — so the cart is
   * re-read before anything is told about it. The drawer and the header
   * bubble both read `detail.cart`, and `source` is anything but 'cart' so
   * assets/ibc-cart.js refreshes AND opens the drawer. */
  Multipack.prototype.announce = function (source) {
    return this.fetchCart()
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('ibc:cart-changed', {
            detail: {
              cart: cart,
              item_count: cart ? cart.item_count : undefined,
              items: cart ? cart.items : undefined,
              total_price: cart ? cart.total_price : undefined,
              source: source
            }
          })
        );
        return cart;
      });
  };

  Multipack.prototype.handleSubmitCapture = function (event) {
    if (!this.form || event.target !== this.form) return;
    if (!this.capacity) return;

    if (this.editing) {
      stop(event);
      this.saveEdit();
      return;
    }

    /* The product engine can do it properly. Let the event through. */
    if (this.ensureHook()) return;

    stop(event);
    this.addOwn();
  };

  Multipack.prototype.busy = function (on) {
    this.root.setAttribute('data-mp-busy', on ? 'true' : 'false');
  };

  /* ---------------------------------------------------------- public API -- */

  Multipack.prototype.setSelection = function (items) {
    for (var i = 0; i < this.tiles.length; i++) this.tiles[i].qty = 0;
    if (items && items.length) {
      for (var j = 0; j < items.length; j++) {
        var rec = items[j];
        for (var k = 0; k < this.tiles.length; k++) {
          if (this.tiles[k].handle === rec.h || this.tiles[k].variantId === rec.id) {
            this.tiles[k].qty = parseInt(rec.q, 10) || 0;
            break;
          }
        }
      }
    }
    this.sync();
  };

  Multipack.prototype.getSelection = function () {
    var picked = this.chosen();
    var out = [];
    for (var i = 0; i < picked.length; i++) {
      out.push({
        id: picked[i].variantId,
        h: picked[i].handle,
        t: picked[i].title,
        q: picked[i].qty,
        p: picked[i].price
      });
    }
    return out;
  };

  /* ------------------------------------------------- editing from the basket */

  /* THE ROUND TRIP, AND WHY IT IS A ROUND TRIP.
   * ------------------------------------------
   * The basket's Edit pack link is this product's own URL with
   * `?selection=<_Selection ID>` appended. Not an inline panel: N picks from a grid of
   * photographs is a page, not a drawer.
   *
   * ADDRESSED BY PACK ID, NEVER BY LINE KEY OR INDEX. /cart/change.js
   * re-keys a line whenever its properties change and can reorder the
   * basket, so both a key and an index read at page load are stale by the
   * time they would be used. The pack id is written by this file and never
   * changes for the life of a pack.
   *
   * IT ADDS THE NEW PACK FIRST AND ZEROES THE OLD ONE SECOND, and it re-reads
   * the basket for the old keys at the moment of saving rather than trusting
   * what it read on page load. If the add fails, nothing has been taken
   * away. If the removal fails, the customer has two packs — visible,
   * nameable and removable — rather than none. The new pack carries a NEW
   * `_Selection ID`, so the new lines cannot merge into the old ones on the way
   * past. */

  function editPackId() {
    try {
      return new URLSearchParams(window.location.search).get(EDIT_PARAM) || '';
    } catch (e) {
      return '';
    }
  }

  Multipack.prototype.membersOf = function (cart, packId) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties;
      if (props && props['_Selection ID'] === packId) out.push(items[i]);
    }
    return out;
  };

  Multipack.prototype.startEditing = function (packId) {
    var self = this;

    this.fetchCart()
      .then(function (cart) {
        var members = self.membersOf(cart, packId);
        if (!members.length) throw new Error('pack');

        var selection = [];
        var packs = 0;

        for (var i = 0; i < members.length; i++) {
          var unit = parseInt(members[i].properties['_Selection Unit'], 10) || 0;
          if (unit < 1) continue;
          selection.push({ id: members[i].variant_id, q: unit });
          /* How many packs this line represents. Every member should agree;
           * the largest is taken so a basket somebody has hand-edited
           * cannot silently shrink the pack on a round trip. */
          var mine = Math.round((members[i].quantity || 0) / unit);
          if (mine > packs) packs = mine;
        }

        if (!selection.length) throw new Error('pack');

        self.setSelection(selection);
        self.setPacksWanted(packs || 1);
        self.editing = packId;
        self.root.setAttribute('data-mp-editing', 'true');
        self.nameTheButton('Update your selection');
      })
      .catch(function () {
        /* The pack has gone, or the basket could not be read. Shown as a
         * builder for a new pack rather than as an error about a basket the
         * reader may already have emptied in another tab. */
        self.editing = '';
        self.root.removeAttribute('data-mp-editing');
      });
  };

  /* The form's own button says Add to basket, and while an existing pack is
   * being edited that is the wrong word for what pressing it will do. */
  Multipack.prototype.nameTheButton = function (text) {
    if (!this.form) return;
    var button = this.form.querySelector('[name="add"]');
    if (!button) button = this.form.querySelector('[type="submit"]');
    if (!button) return;
    var label = button.querySelector('span') || button;
    if (this.priorLabel === null) this.priorLabel = label.textContent;
    label.textContent = text;
  };

  Multipack.prototype.zeroLines = function (keys) {
    if (!keys.length) return Promise.resolve(null);
    var updates = {};
    for (var i = 0; i < keys.length; i++) updates[keys[i]] = 0;

    return fetch(root_url() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) throw new Error('update');
      return response.json();
    });
  };

  Multipack.prototype.saveEdit = function () {
    var self = this;

    if (this.total() !== this.capacity) {
      this.say(this.shortfall());
      var first = this.root.querySelector('[data-mp-more]');
      if (first && first.focus) first.focus();
      return Promise.resolve();
    }

    var items = this.itemsFor(this.packsWanted(), newPack());
    if (!items.length) return Promise.resolve();

    var was = this.editing;
    this.busy(true);

    return this.fetchCart()
      .then(function (cart) {
        var members = self.membersOf(cart, was);
        var keys = [];
        for (var i = 0; i < members.length; i++) keys.push(members[i].key);
        return keys;
      })
      .then(function (keys) {
        return self.postItems(items).then(function () {
          return keys;
        });
      })
      .then(function (keys) {
        return self.zeroLines(keys);
      })
      .then(function () {
        /* Back to the basket the edit came from, which is where the reader
         * expects to land and where they can see that it took. */
        self.leaveTo(root_url() + 'cart');
      })
      .catch(function () {
        self.busy(false);
        self.say('We could not update your selection. Please try again, or add it fresh.');
      });
  };

  /* THE ONLY PLACE THIS FILE NAVIGATES, on purpose. One named method rather
   * than an assignment buried in a promise chain: it is the single most
   * consequential thing the builder does — it leaves the page, unsaved work
   * and all — so it is worth being able to point at, and worth being able to
   * stand in for when this is under test. */
  Multipack.prototype.leaveTo = function (url) {
    window.location.href = url;
  };

  /* ----------------------------------------------------------------- boot -- */

  var live = null;

  function boot() {
    var root = document.querySelector(ROOT);
    if (!root || root.ibcMultipack) return;
    root.ibcMultipack = true;
    live = new Multipack(root);
    live.start();
    window.IbcMultipack = live;

    /* Only when the basket sent us here. On every other visit this is an
     * ordinary product page and nothing is fetched. */
    var packId = editPackId();
    if (packId) live.startEditing(packId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The Theme Editor rebuilds a section in place. Tear the old one down
   * first or its listeners keep running against a detached element — and
   * its submit hook keeps contributing items to a form that is gone. */
  document.addEventListener('shopify:section:unload', function (event) {
    if (!live || !event.target || !event.target.querySelector) return;
    if (!event.target.querySelector(ROOT)) return;
    live.destroy();
    live = null;
    window.IbcMultipack = null;
  });

  document.addEventListener('shopify:section:load', function () {
    boot();
  });
})();
